/* =============================================================
   ImportStudentsModal.jsx — AI-powered bulk student import
   -------------------------------------------------------------
   Supports: .xlsx, .xls, .csv, .pdf, .docx
   Flow:
     1. Admin uploads a file
     2. File is parsed / converted to text (or kept as base64 for PDF)
     3. Sent to Supabase Edge Function → Gemini 2.5 Flash
     4. Preview table shown — admin can edit / delete rows
     5. Confirm → bulk insert via addStudents()
   ============================================================= */

import { useState, useRef, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { getEuStatus, NATIONALITIES } from '../lib/countries';
import { DEGREE_LEVELS } from '../lib/studentCategory';


/* ─── FUZZY MATCH ────────────────────────────────────────────── */
// Returns the partner id whose name best matches uniName, or null.
function fuzzyMatch(uniName, partners) {
  if (!uniName?.trim() || !partners.length) return null;
  const norm = s => s.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
  const q = norm(uniName);

  // 1. Exact match
  let hit = partners.find(p => norm(p.name) === q);
  if (hit) return hit.id;

  // 2. One name fully contains the other
  hit = partners.find(p => { const pn = norm(p.name); return pn.includes(q) || q.includes(pn); });
  if (hit) return hit.id;

  // 3. Significant word overlap (words > 3 chars)
  const qWords = q.split(' ').filter(w => w.length > 3);
  if (qWords.length) {
    hit = partners.find(p => {
      const pWords = norm(p.name).split(' ').filter(w => w.length > 3);
      const common = qWords.filter(w => pWords.includes(w));
      return common.length >= Math.min(2, qWords.length, pWords.length);
    });
    if (hit) return hit.id;
  }
  return null;
}


/* ─── PARTNER LINK CELL ──────────────────────────────────────── */
// States:
//   partnerId set               → green  "Linked"        (existing partner)
//   !partnerId && suggestedName → indigo "Will create"   (stub created at import)
//   !partnerId && !suggestedName→ amber  "Not linked"    (nothing will be created)
// Clicking always opens a search to manually link to an existing partner.
function PartnerLinkCell({ partnerId, partners, suggestedName, onChange }) {
  const [search, setSearch] = useState('');
  const [open,   setOpen]   = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const matched  = partners.find(p => p.id === partnerId);
  const filtered = partners.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));
  const willCreate = !partnerId && !!suggestedName?.trim();

  const chip = matched ? (
    // Linked to existing partner
    <div className="flex items-center gap-1">
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full max-w-[160px]">
        <span className="material-symbols-outlined text-[11px] flex-shrink-0">check_circle</span>
        <span className="truncate">{matched.name}</span>
      </span>
      <button onClick={() => onChange(null)} className="text-slate-300 hover:text-red-400 transition-colors" title="Unlink">
        <span className="material-symbols-outlined text-[13px]">close</span>
      </button>
    </div>
  ) : willCreate ? (
    // Will be auto-created at import
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-indigo-50 text-indigo-600 border border-indigo-200 px-2 py-0.5 rounded-full max-w-[160px] cursor-pointer hover:bg-indigo-100 transition-colors">
      <span className="material-symbols-outlined text-[11px] flex-shrink-0">add_circle</span>
      <span className="truncate">New partner</span>
    </span>
  ) : (
    // No university name — nothing will be created
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full cursor-pointer hover:bg-amber-100 transition-colors">
      <span className="material-symbols-outlined text-[11px]">link_off</span>
      Not linked
    </span>
  );

  return (
    <div ref={ref} className="relative">
      <div onClick={() => setOpen(o => !o)}>{chip}</div>

      {open && (
        <div className="absolute left-0 top-7 z-50 w-64 bg-white rounded-xl shadow-xl border border-slate-100 p-2">
          <p className="text-[10px] text-slate-400 px-1 mb-1.5">Link to an existing partner instead:</p>
          <input
            autoFocus
            type="text"
            placeholder="Search existing partner…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg mb-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-300"
          />
          <div className="max-h-44 overflow-y-auto space-y-0.5">
            {filtered.length === 0
              ? <p className="text-xs text-slate-400 px-2 py-1.5">No existing partners found</p>
              : filtered.map(p => (
                <button
                  key={p.id}
                  onClick={() => { onChange(p.id); setOpen(false); setSearch(''); }}
                  className="w-full text-left px-2.5 py-1.5 text-xs text-[#464555] hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <span className="font-medium">{p.name}</span>
                  {p.country && <span className="text-slate-400 ml-1">· {p.country}</span>}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

const EXCHANGE_TYPES = [
  { value: 'erasmus',       label: 'Erasmus'       },
  { value: 'bilateral',     label: 'Bilateral'     },
  { value: 'double_degree', label: 'Double Degree' },
];

/* ─── FILE PARSER ────────────────────────────────────────────── */
async function parseFileToContent(file) {
  const ext = file.name.split('.').pop().toLowerCase();

  // PDF → base64 (sent as inline_data to Gemini)
  if (ext === 'pdf') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        resolve({ content: base64, fileType: 'application/pdf' });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Excel → convert to CSV text
  if (ext === 'xlsx' || ext === 'xls') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb   = XLSX.read(e.target.result, { type: 'array' });
          const ws   = wb.Sheets[wb.SheetNames[0]];
          const csv  = XLSX.utils.sheet_to_csv(ws);
          resolve({ content: csv, fileType: 'text/csv' });
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // CSV / TXT → plain text
  if (ext === 'csv' || ext === 'txt') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve({ content: e.target.result, fileType: 'text/csv' });
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  // DOCX → extract text via mammoth
  if (ext === 'docx') {
    const mammoth = await import('mammoth');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const result = await mammoth.extractRawText({ arrayBuffer: e.target.result });
          resolve({ content: result.value, fileType: 'text/plain' });
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  throw new Error(`Unsupported file type: .${ext}`);
}


/* ─── EDITABLE CELL ──────────────────────────────────────────── */
function EditableCell({ value, onChange, type = 'text', options }) {
  if (options) {
    return (
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value || null)}
        className="w-full px-1.5 py-1 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300"
      >
        <option value="">—</option>
        {options.map(o => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={e => onChange(e.target.value || null)}
      className="w-full px-1.5 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-300"
    />
  );
}


/* ─── IMPORT STUDENTS MODAL ──────────────────────────────────── */
export default function ImportStudentsModal({ type, onImport, onClose }) {
  const isIncoming = type === 'incoming';
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const [step,          setStep]         = useState('upload');   // 'upload' | 'loading' | 'preview' | 'importing' | 'done'
  const [students,      setStudents]     = useState([]);
  const [fileName,      setFileName]     = useState('');
  const [error,         setError]        = useState(null);
  const [dragging,      setDragging]     = useState(false);
  const [importError,   setImportError]  = useState(null);
  const [partners,      setPartners]     = useState([]);
  const [newPartnerIds, setNewPartnerIds] = useState(new Set());
  const [semesters,     setSemesters]    = useState([]);
  const [semesterId,    setSemesterId]   = useState('');  // selected term for all imported students
  const partnersRef = useRef([]);

  // Fetch partner universities and exchange terms once on mount
  useEffect(() => {
    supabase
      .from('partner_universities')
      .select('id, name, country')
      .then(({ data }) => { if (data) { setPartners(data); partnersRef.current = data; } });
    supabase
      .from('semesters')
      .select('id, name')
      .order('created_at', { ascending: false })
      .then(({ data }) => setSemesters(data ?? []));
  }, []);

  // Keep ref in sync with state
  useEffect(() => { partnersRef.current = partners; }, [partners]);

  /* ── Create a stub partner and return its id ──────────────── */
  const handleCreatePartner = useCallback(async (name) => {
    const trimmed = name.trim();
    const abbr    = trimmed.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase();

    const { data, error: insertError } = await supabase
      .from('partner_universities')
      .insert({
        university_id:  user.universityId,
        name:           trimmed,
        country:        '',
        type:           'BILATERAL',
        connection:     'Manual',
        abbreviation:   abbr,
        max_students:   5,
        contact_emails: [],
        useful_links:   [],
        key_dates:      [],
      })
      .select('id, name, country')
      .single();

    if (insertError) throw insertError;
    setPartners(prev => [...prev, data]);
    setNewPartnerIds(prev => new Set([...prev, data.id]));
    return data.id;
  }, [user?.universityId]);

  /* ── Process file ─────────────────────────────────────────── */
  const processFile = useCallback(async (file) => {
    setFileName(file.name);
    setError(null);
    setStep('loading');

    try {
      const { content, fileType } = await parseFileToContent(file);

      // Call Supabase Edge Function
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = supabase.supabaseUrl;
      const res = await fetch(
        `${supabaseUrl}/functions/v1/ai-import-students`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ fileContent: content, fileType, studentType: type }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        let errMsg = `Edge Function error ${res.status}`;
        try { const j = JSON.parse(text); errMsg = j.error ?? j.message ?? errMsg; } catch {}
        // If we got HTML back the URL is likely wrong
        if (text.trimStart().startsWith('<')) {
          errMsg = `Edge Function not reachable (${res.status}). Check that the function "ai-import-students" is deployed on Supabase.`;
        }
        throw new Error(errMsg);
      }

      const responseText = await res.text();
      let extracted;
      try {
        extracted = JSON.parse(responseText).students;
      } catch {
        throw new Error(`Invalid response from Edge Function. Got: ${responseText.slice(0, 120)}`);
      }

      if (!extracted || extracted.length === 0) {
        throw new Error('No students found in this file. Check that it contains a list of students.');
      }

      // Normalize and auto-match against existing partners
      let normalized = extracted.map((s, i) => {
        const uniName = isIncoming ? s.home_university : s.destination_university;
        return {
          _id:           i,
          name:          s.name          ?? '',
          email:         s.email         ?? '',
          nationality:   s.nationality   ?? '',
          exchange_type: s.exchange_type ?? '',
          degree_level:  s.degree_level  ?? '',
          home_university:        isIncoming  ? (s.home_university        ?? '') : undefined,
          destination_university: !isIncoming ? (s.destination_university ?? '') : undefined,
          partner_id:    fuzzyMatch(uniName, partnersRef.current),
        };
      });

      setStudents(normalized);
      setStep('preview');
    } catch (err) {
      setError(err.message ?? String(err));
      setStep('upload');
    }
  }, [type, isIncoming]);

  /* ── Drag & drop ──────────────────────────────────────────── */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  /* ── Edit a row ───────────────────────────────────────────── */
  const updateRow = (id, field, value) => {
    setStudents(prev => prev.map(s => s._id === id ? { ...s, [field]: value } : s));
  };

  const deleteRow = (id) => {
    setStudents(prev => prev.filter(s => s._id !== id));
  };

  /* ── Confirm import ───────────────────────────────────────── */
  const handleImport = async () => {
    setImportError(null);
    setStep('importing');
    try {
      const valid = students.filter(s => s.name?.trim());

      // Step 1: auto-create stubs for universities without a partner_id
      const uniqueUnmatched = [
        ...new Set(
          valid
            .filter(s => !s.partner_id)
            .map(s => (isIncoming ? s.home_university : s.destination_university)?.trim())
            .filter(Boolean)
        ),
      ];

      let nameToId = {};
      if (uniqueUnmatched.length > 0 && user?.universityId) {
        const toCreate = uniqueUnmatched.map(n => ({
          university_id:  user.universityId,
          name:           n,
          country:        '',
          type:           'BILATERAL',
          connection:     'Manual',
          abbreviation:   n.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase(),
          max_students:   5,
          contact_emails: [],
          useful_links:   [],
          key_dates:      [],
        }));
        const { data: created } = await supabase
          .from('partner_universities')
          .insert(toCreate)
          .select('id, name');
        if (created?.length) {
          nameToId = Object.fromEntries(created.map(c => [c.name, c.id]));
          setNewPartnerIds(new Set(created.map(c => c.id)));
        }
      }

      // Step 2: build rows with resolved partner_ids
      const rows = valid.map(s => {
        const uniName = (isIncoming ? s.home_university : s.destination_university)?.trim();
        return {
          name:         s.name.trim(),
          email:        s.email?.trim() || null,
          nationality:  s.nationality   || null,
          euStatus:     getEuStatus(s.nationality),
          exchangeType: s.exchange_type || null,
          degreeLevel:  s.degree_level  || null,
          uni:          s.home_university        || null,
          dest:         s.destination_university || null,
          semesterId:   semesterId || null,
          partnerId:    s.partner_id ?? nameToId[uniName] ?? null,
        };
      });

      await onImport(rows);
      setStep('done');
      setTimeout(onClose, 1500);
    } catch (err) {
      setImportError(err.message ?? String(err));
      setStep('preview');
    }
  };

  const validCount = students.filter(s => s.name?.trim()).length;

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px] text-indigo-600">auto_awesome</span>
            <h2 className="font-display text-base font-semibold text-[#0b1c30]">
              AI Import — {isIncoming ? 'Incoming' : 'Outgoing'} Students
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {step === 'preview' && (
              <span className="text-xs text-slate-400">
                {validCount} student{validCount !== 1 ? 's' : ''} ready to import
              </span>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
              <span className="material-symbols-outlined text-[20px]">close</span>
            </button>
          </div>
        </div>

        {/* ── STEP: UPLOAD ─────────────────────────────────────── */}
        {step === 'upload' && (
          <div className="p-8 flex flex-col items-center justify-center flex-1">
            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`w-full max-w-lg border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${
                dragging
                  ? 'border-indigo-400 bg-indigo-50'
                  : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'
              }`}
            >
              <span className="material-symbols-outlined text-[48px] text-slate-300 mb-3 block">upload_file</span>
              <p className="text-sm font-semibold text-[#0b1c30] mb-1">
                Drop your file here or click to browse
              </p>
              <p className="text-xs text-slate-400">
                Supports Excel (.xlsx), CSV, PDF, Word (.docx) — any language
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.pdf,.docx,.txt"
                className="hidden"
                onChange={e => { if (e.target.files[0]) processFile(e.target.files[0]); }}
              />
            </div>

            {error && (
              <div className="mt-4 w-full max-w-lg flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                <span className="material-symbols-outlined text-red-500 text-[16px] flex-shrink-0 mt-0.5">error</span>
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            <p className="mt-6 text-xs text-slate-400 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px]">lock</span>
              File processed securely — AI reads only column headers and student data
            </p>
          </div>
        )}

        {/* ── STEP: LOADING ─────────────────────────────────────── */}
        {step === 'loading' && (
          <div className="p-12 flex flex-col items-center justify-center flex-1 gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-indigo-100" />
              <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="material-symbols-outlined text-[20px] text-indigo-600">auto_awesome</span>
              </span>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-[#0b1c30]">AI is reading your file…</p>
              <p className="text-xs text-slate-400 mt-1">{fileName}</p>
            </div>
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 max-w-sm text-center">
              <span className="material-symbols-outlined text-amber-500 text-[16px] mt-0.5 flex-shrink-0">schedule</span>
              <p className="text-xs text-amber-700">
                This may take <strong>1–2 minutes</strong> depending on file size.<br />
                Please don't close or refresh the page.
              </p>
            </div>
          </div>
        )}

        {/* ── STEP: PREVIEW ─────────────────────────────────────── */}
        {step === 'preview' && (
          <>
            {/* Exchange Term selector — applies to all imported students */}
            <div className="flex items-center gap-4 px-6 py-3 bg-indigo-50 border-b border-indigo-200 flex-shrink-0">
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="material-symbols-outlined text-[16px] text-indigo-600">event_repeat</span>
                <span className="text-xs font-semibold text-indigo-800">Exchange Term</span>
              </div>
              {semesters.length === 0 ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg">
                  No terms created yet — go to <strong>Settings → Exchange Terms</strong> to create one.
                </p>
              ) : (
                <select
                  value={semesterId}
                  onChange={e => setSemesterId(e.target.value)}
                  className={`text-xs rounded-lg px-3 py-1.5 bg-white text-[#0b1c30] focus:outline-none focus:ring-2 focus:ring-indigo-300 cursor-pointer border ${
                    !semesterId ? 'border-red-300 ring-1 ring-red-200' : 'border-indigo-200'
                  }`}
                >
                  <option value="">— Select a term —</option>
                  {semesters.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
              {!semesterId && semesters.length > 0 && (
                <span className="text-[10px] font-semibold text-red-500 flex items-center gap-1">
                  <span className="material-symbols-outlined text-[12px]">error</span>
                  Required before importing
                </span>
              )}
              {semesterId && (
                <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                  All {students.filter(s => s.name?.trim()).length} students → {semesters.find(s => s.id === semesterId)?.name}
                </span>
              )}
            </div>

            {/* New partner banner — shown only when there are unmatched universities */}
            {students.some(s => !s.partner_id && !!(isIncoming ? s.home_university : s.destination_university)?.trim()) && (
              <div className="flex items-start gap-3 px-6 py-3 bg-slate-50 border-b border-slate-200 flex-shrink-0">
                <span className="material-symbols-outlined text-indigo-500 text-[18px] mt-0.5 flex-shrink-0">info</span>
                <p className="text-xs text-indigo-700 leading-relaxed">
                  Universities marked as <span className="inline-flex items-center gap-0.5 font-semibold bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full text-[10px] mx-0.5"><span className="material-symbols-outlined text-[10px]">add_circle</span>New partner</span> don't exist yet in your system — they will be automatically added to your <strong>Partner Universities</strong> panel when you confirm the import. Click any chip to link to an existing partner instead.
                </p>
              </div>
            )}

            <div className="flex items-center gap-3 px-6 py-3 bg-slate-50 border-b border-slate-100 flex-shrink-0">
              <span className="material-symbols-outlined text-indigo-500 text-[16px]">info</span>
              <p className="text-xs text-indigo-700 flex-1">
                Review the extracted data. Click any cell to edit, or delete rows you don't need. Then confirm the import.
              </p>
              {(() => {
                const linked   = students.filter(s => s.partner_id).length;
                const unlinked = students.length - linked;
                return (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {linked > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                        <span className="material-symbols-outlined text-[11px]">check_circle</span>
                        {linked} linked
                      </span>
                    )}
                    {unlinked > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        <span className="material-symbols-outlined text-[11px]">link_off</span>
                        {unlinked} not linked
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Table */}
            <div className="overflow-auto flex-1 p-4">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-3 py-2 font-semibold text-slate-500 rounded-tl-lg border-b border-slate-200">Name</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-500 border-b border-slate-200">Email</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-500 border-b border-slate-200">Nationality</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-500 border-b border-slate-200">Exchange Type</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-500 border-b border-slate-200">Degree</th>
                    {isIncoming
                      ? <th className="text-left px-3 py-2 font-semibold text-slate-500 border-b border-slate-200">Home University</th>
                      : <th className="text-left px-3 py-2 font-semibold text-slate-500 border-b border-slate-200">Destination University</th>
                    }
                    <th className="text-left px-3 py-2 font-semibold text-slate-500 border-b border-slate-200">Partner Link</th>
                    <th className="px-3 py-2 border-b border-slate-200 rounded-tr-lg" />
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, i) => {
                    const missingName = !s.name?.trim();
                    return (
                      <tr key={s._id} className={`border-b border-slate-100 ${missingName ? 'bg-red-50' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                        <td className="px-2 py-1.5 min-w-[140px]">
                          <EditableCell value={s.name} onChange={v => updateRow(s._id, 'name', v)} />
                          {missingName && <p className="text-[10px] text-red-500 mt-0.5">Required</p>}
                        </td>
                        <td className="px-2 py-1.5 min-w-[160px]">
                          <EditableCell value={s.email} onChange={v => updateRow(s._id, 'email', v)} />
                        </td>
                        <td className="px-2 py-1.5 min-w-[130px]">
                          <EditableCell
                            value={s.nationality}
                            onChange={v => updateRow(s._id, 'nationality', v)}
                            options={NATIONALITIES.map(n => ({ value: n, label: n }))}
                          />
                        </td>
                        <td className="px-2 py-1.5 min-w-[130px]">
                          <EditableCell
                            value={s.exchange_type}
                            onChange={v => updateRow(s._id, 'exchange_type', v)}
                            options={EXCHANGE_TYPES}
                          />
                        </td>
                        <td className="px-2 py-1.5 min-w-[110px]">
                          <EditableCell
                            value={s.degree_level}
                            onChange={v => updateRow(s._id, 'degree_level', v)}
                            options={DEGREE_LEVELS}
                          />
                        </td>
                        <td className="px-2 py-1.5 min-w-[160px]">
                          {isIncoming
                            ? <EditableCell value={s.home_university}        onChange={v => updateRow(s._id, 'home_university', v)} />
                            : <EditableCell value={s.destination_university} onChange={v => updateRow(s._id, 'destination_university', v)} />
                          }
                        </td>
                        <td className="px-2 py-1.5 min-w-[180px]">
                          <PartnerLinkCell
                            partnerId={s.partner_id}
                            partners={partners}
                            suggestedName={isIncoming ? s.home_university : s.destination_university}
                            onChange={v => updateRow(s._id, 'partner_id', v)}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <button
                            onClick={() => deleteRow(s._id)}
                            className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <span className="material-symbols-outlined text-[14px]">delete</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
              <button
                onClick={() => { setStudents([]); setStep('upload'); }}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                Upload different file
              </button>
              <div className="flex items-center gap-3">
                {newPartnerIds.size > 0 && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">warning</span>
                    {newPartnerIds.size} new partner{newPartnerIds.size > 1 ? 's' : ''} created — complete their profiles in Partners after import.
                  </p>
                )}
                {importError && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">error</span>
                    {importError}
                  </p>
                )}
                <button
                  onClick={handleImport}
                  disabled={validCount === 0 || !semesterId}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#4343d5] text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-40"
                >
                  <span className="material-symbols-outlined text-[16px]">upload</span>
                  Import {validCount} student{validCount !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── STEP: IMPORTING ───────────────────────────────────── */}
        {step === 'importing' && (
          <div className="p-12 flex flex-col items-center justify-center flex-1 gap-4">
            <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-semibold text-[#0b1c30]">Importing students…</p>
          </div>
        )}

        {/* ── STEP: DONE ────────────────────────────────────────── */}
        {step === 'done' && (
          <div className="p-12 flex flex-col items-center justify-center flex-1 gap-3">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <span className="material-symbols-outlined text-emerald-600 text-[28px]">check_circle</span>
            </div>
            <p className="text-sm font-semibold text-[#0b1c30]">
              {validCount} student{validCount !== 1 ? 's' : ''} imported successfully!
            </p>
            {newPartnerIds.size > 0 && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 max-w-sm text-center">
                <span className="material-symbols-outlined text-amber-500 text-[16px] mt-0.5 flex-shrink-0">warning</span>
                <p className="text-xs text-amber-700">
                  <strong>{newPartnerIds.size} new partner universit{newPartnerIds.size > 1 ? 'ies were' : 'y was'} created.</strong>
                  {' '}Remember to complete their profiles in the Partners panel.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
